import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import { EmptyState, RecordSection } from '../../components/record/RecordPage.tsx';
import { NoRelationshipHistory } from '../../relationships/NoRelationshipHistory.tsx';
import { useMailAccounts } from '../../settings/useMailAccounts.ts';
import { RelationshipStateChip } from '../../relationships/RelationshipStateChip.tsx';
import { WhoKnowsThemList } from '../../relationships/WhoKnowsThemList.tsx';
import {
  daysSince,
  fetchContactPaths,
  formatDaysAgo,
  type RelationshipPath,
  type ScoreComponent
} from '../../relationships/relationshipApi.ts';
import { getErrorMessage } from '../../utils/helperUtils.ts';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const ComponentTile = ({
  label,
  part,
  detail,
  warn = false
}: {
  label: string;
  part: ScoreComponent;
  detail: string;
  warn?: boolean;
}) => (
  <Box
    sx={{ p: 1.25, height: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {part.points}
        <Typography component="span" variant="caption" color="text.secondary">
          /{part.max}
        </Typography>
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={part.max > 0 ? Math.min(100, (part.points / part.max) * 100) : 0}
      aria-label={`${label}: ${part.points} of ${part.max}`}
      sx={{ my: 0.75, height: 6, borderRadius: 3 }}
    />
    <Typography
      variant="body2"
      color={warn ? 'warning.main' : 'text.secondary'}
      sx={warn ? { fontWeight: 600 } : undefined}
    >
      {detail}
    </Typography>
  </Box>
);

// Show the four parts, not just the number, so the score explains itself.
const YourScore = ({
  path,
  calendarConnected
}: {
  path: RelationshipPath;
  calendarConnected: boolean;
}) => {
  const exchangeDays = path.noExchangeYet ? null : daysSince(path.lastExchangeAt);
  const tiles = [
    {
      label: 'Recency',
      part: path.components.recency,
      detail:
        exchangeDays === null ? 'No reply yet' : `Last exchange ${formatDaysAgo(exchangeDays)}`,
      warn: exchangeDays === null
    },
    {
      label: 'Frequency',
      part: path.components.frequency,
      detail: path.touches
        ? `${count(path.touches, 'email', 'emails')} in the last 90 days`
        : 'No email in the last 90 days'
    },
    {
      label: 'Reciprocity',
      part: path.components.reciprocity,
      detail: `${path.inbound} from them, ${path.outbound} from you`
    },
    {
      label: 'Meetings',
      part: path.components.meetings,
      detail: path.meetings
        ? `${count(path.meetings, 'meeting', 'meetings')} in the last 90 days`
        : calendarConnected
          ? 'No meetings in the last 90 days'
          : 'Calendar not connected yet'
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography
          sx={{ fontSize: '2rem', fontWeight: 750, lineHeight: 1, letterSpacing: '-0.025em' }}
        >
          {path.score}
        </Typography>
        <Box>
          <RelationshipStateChip state={path.state} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {path.noExchangeYet
              ? 'Your score out of 100. No reply yet, so it stays below Warm.'
              : 'Your score out of 100'}
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={1.5}>
        {tiles.map((tile) => (
          <Grid key={tile.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <ComponentTile {...tile} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const ContactRelationshipSection = ({
  contactId,
  hasEmail
}: {
  contactId: number;
  hasEmail: boolean;
}) => {
  const [paths, setPaths] = useState<RelationshipPath[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setPaths(null);
    setError(null);
    fetchContactPaths(contactId)
      .then((rows) => {
        if (current) setPaths(rows);
      })
      .catch((err) => {
        if (current) setError(getErrorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [contactId]);

  const yours = paths?.find((path) => path.isYou) ?? null;
  const { accounts } = useMailAccounts();
  const calendarConnected = accounts.some((a) => a.touchScanEnabled && a.canReadCalendar);

  return (
    <RecordSection
      title="Relationship"
      description="Scored from who emailed whom, and when, in connected mailboxes."
    >
      {error ? (
        <Alert severity="error">Could not load the relationship: {error}</Alert>
      ) : !paths ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading relationship
          </Typography>
        </Box>
      ) : !paths.length ? (
        hasEmail ? (
          <NoRelationshipHistory who="them" />
        ) : (
          <EmptyState>
            This contact has no email address on file, so there is no email history to score.
          </EmptyState>
        )
      ) : (
        <Stack spacing={2}>
          {yours ? (
            <YourScore path={yours} calendarConnected={calendarConnected} />
          ) : (
            <EmptyState>You have no email history with them.</EmptyState>
          )}
          <Box>
            <Typography variant="subtitle2">Who here knows them</Typography>
            <WhoKnowsThemList paths={paths} />
          </Box>
        </Stack>
      )}
    </RecordSection>
  );
};
